import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { JobDetails } from '../../../shared/models/job-data.model';
import { RECLASSIFICATION_QUALIFYING_STATUSES } from '../../../shared/config/constants';

export interface ReclassificationDisplayItem {
  documentName: string;
  displayText: string;
  isReclassified: boolean;
  originalLabel: string;
  revisedLabel: string | null;
  ariaLabel: string;
}

const NO_MODIFICATIONS_SENTINEL = 'No modifications made to DTP review.';

@Component({
  selector: 'app-document-requirement-changes',
  imports: [MatIcon],
  templateUrl: './document-requirement-changes.component.html',
  styleUrl: './document-requirement-changes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentRequirementChangesComponent {
  readonly detail = input.required<JobDetails>();

  readonly isExpanded = signal(false);

  readonly isVisible = computed(() => {
    const d = this.detail();
    return (
      !!d.quote &&
      RECLASSIFICATION_QUALIFYING_STATUSES.has(d.jobStatus)
    );
  });

  readonly reclassificationItems = computed<ReclassificationDisplayItem[]>(
    () => {
      const files = this.detail().files ?? [];
      const filesByName = new Map<string, ReclassificationDisplayItem>();

      for (const file of files) {
        if (file.fileType && file.fileType !== 'file') {
          continue;
        }

        const key = (file.uploadName || file.name || '--').trim();
        if (filesByName.has(key)) {
          continue;
        }

        const originalIsDtp = file.isDtpFile === true;
        const revisedIsDtp = file.isDtpFileRevised === true;
        const originalLabel = originalIsDtp ? 'DTP' : 'Basic Translation';
        const revisedLabel = revisedIsDtp ? 'DTP' : 'Basic Translation';
        const isReclassified = originalIsDtp !== revisedIsDtp;

        if (isReclassified) {
          filesByName.set(key, {
            documentName: key,
            displayText: `${originalLabel} → ${revisedLabel}`,
            isReclassified: true,
            originalLabel,
            revisedLabel,
            ariaLabel: `changed from ${originalLabel} to ${revisedLabel}`,
          });
        } else {
          filesByName.set(key, {
            documentName: key,
            displayText: `No Change (${originalLabel})`,
            isReclassified: false,
            originalLabel,
            revisedLabel: null,
            ariaLabel: `No change, ${originalLabel}`,
          });
        }
      }

      return Array.from(filesByName.values());
    },
  );

  readonly hasReclassification = computed(() =>
    this.reclassificationItems().some((item) => item.isReclassified),
  );

  readonly showJustification = computed(() => {
    if (!this.hasReclassification()) {
      return false;
    }
    const justification = this.detail().changeJustificationDtp;
    return (
      !!justification &&
      justification.trim().length > 0 &&
      justification !== NO_MODIFICATIONS_SENTINEL
    );
  });

  toggleSection(): void {
    this.isExpanded.update((v) => !v);
  }
}
